<!-- eslint-disable vue/no-v-html -->
<template>
  <div>
    <OnboardingPopover
      :is-visible="showOnboardingPopover"
      :on-prev="() => $router.push('/boeken')"
      position="topleft"
      mobile-position="top"
      :mobile-position-breakpoint="700"
      :title="$label(`${onboardingLabel}.title`)"
      :text="$label(`${onboardingLabel}.content`)"
      :extra-items="[
        {
          text: $label(`${onboardingLabel}.infoText`),
          href: $label(`${onboardingLabel}.infoHref`)
        }
      ]"
    >
      <BaseButton
        class="reset-btn-styling download-btn d-flex align-items-center text-start"
        variant="link"
        data-testid="download-btn"
        @click="showModal"
      >
        <DownloadIcon
          id="download-icon"
          width="20"
          height="20"
          class="download-icon"
        />
        {{ $label('books.download') }}
      </BaseButton>
    </OnboardingPopover>

    <BaseModal
      id="downloadModal"
      header-class="bg-soft"
      hide-footer
      :static-modal="staticModal"
    >
      <template #header>
        <div
          class="w-100 d-flex justify-content-between align-items-center p-8"
        >
          <div>
            <div class="text-large mb-8">
              {{ book.title }}
            </div>

            <span v-if="book.author">
              {{ book.author.name }}
            </span>
          </div>

          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="modal.hide"
          ></button>
        </div>
      </template>

      <template #body>
        <div class="p-8">
          <div class="mb-16s">
            <span class="fw-semibold">
              {{ $label('books.chooseDownloadFormat') }}
            </span>
          </div>
          <div class="mb-16">
            <BaseSelect
              :options="options"
              custom-width="320px"
              :selected-value="selectedOption"
              :on-change="onOptionChange"
            />
          </div>
          <NuxtLink to="/help/downloaden">
            {{ $label('books.downloadHelp') }}
          </NuxtLink>

          <div class="download-action-buttons mt-40">
            <BaseButton
              variant="primary"
              class="download-confirm-btn"
              data-testid="download-confirm-btn"
              @click="download"
            >
              {{ $label('books.download') }}
            </BaseButton>
            <BaseButton class="download-reject-btn" @click="modal.hide">
              {{ $label('books.downloadReject') }}
            </BaseButton>
          </div>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useOnboardingStore } from '~/stores/onboarding'
import { useBooksStore } from '~/stores/books'
import { useUserStore } from '~/stores/user'
import { ref, toRefs } from 'vue'
import { downloadFile } from '~/utils/download'
import {
  useDownloadCounter,
  useDownloadOptions
} from '~/composables/bookDownloads'
import BaseSelect from '~/components/BaseSelect'
import OnboardingPopover from '~/components/onboarding/OnboardingPopover'
import DownloadIcon from '~/assets/icons/download.svg'
import { ONBOARDING_VARIANTS } from '~/utils/constants'
import { useModal } from '~/composables/useModal'

export default {
  components: {
    BaseSelect,
    OnboardingPopover,
    DownloadIcon
  },
  props: {
    book: {
      type: Object,
      required: true
    },
    audiobookDownloadable: {
      type: Boolean,
      default: true
    },
    ebookDownloadable: {
      type: Boolean,
      default: true
    },
    staticModal: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const options = useDownloadOptions(props)
    const selectedOption = ref(options.value[0].id)
    const onOptionChange = (option) => (selectedOption.value = option)
    const { book } = toRefs(props)
    const modal = useModal('downloadModal')

    return {
      options,
      ebookDownloads: useDownloadCounter(book.value, 'ebook'),
      audiobookDownloads: useDownloadCounter(book.value, 'audioBook'),
      selectedOption,
      onOptionChange,
      modal
    }
  },
  computed: {
    ...mapState(useOnboardingStore, {
      onboardingVariant: 'variant',
      onboardingStep: 'step',
      onboardingLabel: 'label'
    }),
    ...mapState(useUserStore, ['loggedInUser']),
    showOnboardingPopover() {
      return (
        (this.onboardingVariant === ONBOARDING_VARIANTS.premium &&
          this.onboardingStep === 4) ||
        (this.onboardingVariant === ONBOARDING_VARIANTS.basicChosenBook &&
          this.onboardingStep === 2)
      )
    }
  },
  methods: {
    ...mapActions(useBooksStore, [
      'getEbookDownloadUrl',
      'getAudioBookDownloadUrl'
    ]),
    download() {
      if (this.selectedOption === 'audiobook') {
        return this.checkAudioDownloadsLeft()
      }

      return this.checkEbookDownloadsLeft()
    },
    showErrorAlert(type) {
      const errorMessage =
        type === 'audio'
          ? 'common.downloads.reachLimitAudioBook.title'
          : 'common.downloads.reachLimitEbook.title'
      this.$toast.error(this.$label(errorMessage))
    },
    checkEbookDownloadsLeft() {
      if (this.ebookDownloads.downloadsLeft()) {
        this.downloadEbook()
      } else {
        this.modal.hide()
        this.showErrorAlert('ebook')
      }
    },
    checkAudioDownloadsLeft() {
      if (this.audiobookDownloads.downloadsLeft()) {
        this.downloadAudioBook()
      } else {
        this.modal.hide()
        this.showErrorAlert('audio')
      }
    },
    onError(error) {
      this.modal.hide()
      this.$toast.error(this.$label('messages.errorOccuredMessage'))
      this.$sentryCaptureException(error)
    },
    async downloadEbook() {
      try {
        this.modal.hide()
        this.$toast.show(this.$label('common.downloads.downloadBegin'))

        const { id, categoryCode, title } = this.book
        const response = await this.getEbookDownloadUrl({
          id,
          categoryCode,
          format: 'ePub',
          language: 'nl-NL'
        })

        if (!response.isUserAllowToDownload) {
          return this.showErrorAlert('ebook')
        }

        downloadFile({
          url: response.fileUrl,
          name: title
        })

        this.ebookDownloads.incrementDownloadCount()
      } catch (error) {
        this.onError(error)
      }
    },
    async downloadAudioBook() {
      try {
        this.modal.hide()
        this.$toast.show(this.$label('common.downloads.downloadBegin'))

        const {
          categoryCode,
          title,
          download: {
            audioBook: { id }
          }
        } = this.book

        const response = await this.getAudioBookDownloadUrl({
          id,
          categoryCode,
          format: 'mp3',
          language: 'nl-NL'
        })

        if (!response.isUserAllowToDownload) {
          return this.showErrorAlert('audio')
        }

        downloadFile({
          url: response.fileUrl,
          name: title
        })

        this.audiobookDownloads.incrementDownloadCount()
      } catch (error) {
        this.onError(error)
      }
    },
    showModal() {
      this.modal.show()
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.modal-header),
:deep(.modal-body) {
  padding: 1rem 1rem;
}

.download-btn {
  text-decoration: underline;
  color: $black;
}

.download-icon {
  margin-right: 8px;
}

.close {
  opacity: 1;
}

.download-confirm-btn {
  margin-right: 20px;
}

.bc-popover-visible {
  .btn {
    color: $white;
    z-index: 17;
    position: relative;
  }

  svg {
    stroke: $white;
  }
}

@include media-breakpoint-down(md) {
  .download-action-buttons {
    display: flex;
    flex-direction: column;
  }

  .download-confirm-btn {
    margin-right: 0px;
  }

  .download-reject-btn {
    margin-top: 18px;
  }
}
</style>
